"use client";

import { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  CircleUser,
  Sparkles,
  ChevronRight,
} from "lucide-react";

interface AiAnalysisData {
  suggestions: string[];
  responsible_entities: string[];
}
interface AiAnalysisProps {
  ai_analysis: AiAnalysisData | null;
}

export default function AiAnalysis(props: AiAnalysisProps) {
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [associationsOpen, setAssociationsOpen] = useState(true);
  const [showAllSuggestionItems, setShowAllSuggestionItems] = useState(false);
  const [showAllAssociationItems, setShowAllAssociationItems] = useState(false);
  const [expandedSuggestions, setExpandedSuggestions] = useState<{
    [key: number]: boolean;
  }>({});
  const [expandedAssociations, setExpandedAssociations] = useState<{
    [key: number]: boolean;
  }>({});
  const suggestionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const associationRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const MAX_LINES = 3;
  const INITIAL_SUGGESTIONS_COUNT = 2;
  const INITIAL_ASSOCIATIONS_COUNT = 2;
  const [needsShowMore, setNeedsShowMore] = useState<{
    [key: number]: boolean;
  }>({});
  const [needsShowMoreAssociations, setNeedsShowMoreAssociations] = useState<{
    [key: number]: boolean;
  }>({});

  useEffect(() => {
    // Check which suggestions need "show more" button after render
    const checkOverflow = () => {
      if (!suggestionsOpen) return;

      const newNeedsShowMore: { [key: number]: boolean } = {};
      // Check all displayed suggestions - use the same logic as displayedSuggestions
      const totalSuggestions = props.ai_analysis?.suggestions?.length || 0;
      const suggestionsToCheck = (props.ai_analysis?.suggestions || []).slice(
        0,
        showAllSuggestionItems ? totalSuggestions : INITIAL_SUGGESTIONS_COUNT
      );

      suggestionsToCheck.forEach((_, displayIdx) => {
        // The original index matches display index since we're slicing from the start
        const originalIdx = displayIdx;
        const ref = suggestionRefs.current[originalIdx];
        if (ref) {
          // Create a clone to measure full height without affecting the original
          const clone = ref.cloneNode(true) as HTMLElement;
          clone.style.position = "absolute";
          clone.style.visibility = "hidden";
          clone.style.height = "auto";
          clone.style.maxHeight = "none";
          clone.style.webkitLineClamp = "none";
          clone.style.display = "block";
          clone.style.overflow = "visible";
          clone.style.textOverflow = "clip";

          ref.parentElement?.appendChild(clone);
          const fullHeight = clone.offsetHeight;
          ref.parentElement?.removeChild(clone);

          const lineHeight =
            parseFloat(window.getComputedStyle(ref).lineHeight) || 24;
          const maxHeight = lineHeight * MAX_LINES;

          newNeedsShowMore[originalIdx] = fullHeight > maxHeight + 5;
        }
      });
      setNeedsShowMore(newNeedsShowMore);
    };

    // Check after DOM is ready
    const timeoutId = setTimeout(checkOverflow, 150);
    return () => clearTimeout(timeoutId);
  }, [props.ai_analysis?.suggestions, suggestionsOpen, showAllSuggestionItems]);

  useEffect(() => {
    // Check which associations need "show more" button after render
    const checkAssociationOverflow = () => {
      if (!associationsOpen) return;

      const newNeedsShowMore: { [key: number]: boolean } = {};
      // Check all displayed associations - use the same logic as displayedAssociations
      const totalAssociations =
        props.ai_analysis?.responsible_entities?.length || 0;
      const associationsToCheck = (
        props.ai_analysis?.responsible_entities || []
      ).slice(
        0,
        showAllAssociationItems ? totalAssociations : INITIAL_ASSOCIATIONS_COUNT
      );

      associationsToCheck.forEach((_, displayIdx) => {
        // The original index matches display index since we're slicing from the start
        const originalIdx = displayIdx;
        const ref = associationRefs.current[originalIdx];
        if (ref) {
          // Create a clone to measure full height without affecting the original
          const clone = ref.cloneNode(true) as HTMLElement;
          clone.style.position = "absolute";
          clone.style.visibility = "hidden";
          clone.style.height = "auto";
          clone.style.maxHeight = "none";
          clone.style.webkitLineClamp = "none";
          clone.style.display = "block";
          clone.style.overflow = "visible";
          clone.style.textOverflow = "clip";

          ref.parentElement?.appendChild(clone);
          const fullHeight = clone.offsetHeight;
          ref.parentElement?.removeChild(clone);

          const lineHeight =
            parseFloat(window.getComputedStyle(ref).lineHeight) || 24;
          const maxHeight = lineHeight * MAX_LINES;

          newNeedsShowMore[originalIdx] = fullHeight > maxHeight + 5;
        }
      });
      setNeedsShowMoreAssociations(newNeedsShowMore);
    };

    // Check after DOM is ready
    const timeoutId = setTimeout(checkAssociationOverflow, 150);
    return () => clearTimeout(timeoutId);
  }, [
    props.ai_analysis?.responsible_entities,
    associationsOpen,
    showAllAssociationItems,
  ]);

  const toggleSuggestion = (idx: number) => {
    setExpandedSuggestions((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const toggleAssociation = (idx: number) => {
    setExpandedAssociations((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  if (!props.ai_analysis) return null;
  const totalSuggestions = props.ai_analysis.suggestions?.length || 0;
  const totalAssociations = props.ai_analysis.responsible_entities?.length || 0;
  const displayedSuggestions = (props.ai_analysis.suggestions || []).slice(
    0,
    showAllSuggestionItems ? totalSuggestions : INITIAL_SUGGESTIONS_COUNT
  );
  const displayedAssociations = (
    props.ai_analysis.responsible_entities || []
  ).slice(
    0,
    showAllAssociationItems ? totalAssociations : INITIAL_ASSOCIATIONS_COUNT
  );

  return (
    <div className="flex flex-col mt-3 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-[hsl(218,37.90%,5.70%)] shadow-lg">
      {/* Header */}
      <div className="w-full bg-gradient-to-r from-purple-600/20 to-pink-600/20 dark:from-cyan-900/30 dark:to-emerald-900/30 border-b border-gray-200 dark:border-gray-800 py-3 px-4">
        <div className="flex flex-row items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500 dark:text-purple-400" />
          <span className="font-semibold text-gray-900 dark:text-white text-lg">
            AI Analysis
          </span>
        </div>
      </div>

      <div className="bg-white dark:bg-[hsl(218,37.90%,5.70%)] p-4 space-y-4">
        {/* Suggestions Section */}
        <div className="flex flex-col">
          <button
            className="flex flex-row items-center justify-between w-full mb-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setSuggestionsOpen(!suggestionsOpen)}
          >
            <span className="font-semibold text-gray-800 dark:text-gray-200 text-base">
              Suggestions
            </span>
            {suggestionsOpen ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {suggestionsOpen && (
            <div className="flex flex-col gap-3">
              {displayedSuggestions.map((suggestion, displayIdx) => {
                // Calculate the original index in the full suggestions array
                const originalIdx = showAllSuggestionItems
                  ? displayIdx
                  : displayIdx;
                const isExpanded = expandedSuggestions[originalIdx];
                return (
                  <div
                    key={originalIdx}
                    className="group relative rounded-lg overflow-hidden bg-gradient-to-r from-purple-600/10 via-purple-500/10 to-pink-500/10 dark:from-cyan-900/20 dark:via-cyan-800/20 dark:to-emerald-800/20 border border-purple-200/50 dark:border-cyan-800/50 hover:border-purple-300 dark:hover:border-emerald-700 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="flex items-start gap-3 p-4">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 dark:from-cyan-500 dark:to-emerald-500 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div
                          ref={(el) => {
                            suggestionRefs.current[originalIdx] = el;
                          }}
                          className={`text-gray-800 dark:text-gray-200 text-sm leading-6 transition-all duration-200 ${
                            !isExpanded ? "line-clamp-3" : ""
                          }`}
                          style={{
                            display: !isExpanded ? "-webkit-box" : "block",
                            WebkitLineClamp: !isExpanded
                              ? MAX_LINES
                              : undefined,
                            WebkitBoxOrient: !isExpanded
                              ? "vertical"
                              : undefined,
                            overflow: !isExpanded ? "hidden" : "visible",
                          }}
                        >
                          {suggestion}
                        </div>
                        {needsShowMore[originalIdx] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSuggestion(originalIdx);
                            }}
                            className="mt-2 text-sm font-medium text-purple-600 dark:text-cyan-400 hover:text-purple-700 dark:hover:text-emerald-300 transition-colors"
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {totalSuggestions > INITIAL_SUGGESTIONS_COUNT && (
                <button
                  onClick={() => setShowAllSuggestionItems((v) => !v)}
                  className="mt-1 self-center inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-cyan-400 hover:text-purple-700 dark:hover:text-emerald-300"
                >
                  {showAllSuggestionItems ? (
                    <>
                      Show less <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Show more ({totalSuggestions - INITIAL_SUGGESTIONS_COUNT})
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Possible Associations Section */}
        <div className="flex flex-col pt-2 border-t border-gray-200 dark:border-gray-800">
          <button
            className="flex flex-row items-center justify-between w-full mb-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setAssociationsOpen(!associationsOpen)}
          >
            <span className="font-semibold text-gray-800 dark:text-gray-200 text-base">
              Possible Associations
            </span>
            {associationsOpen ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {associationsOpen && (
            <div className="flex flex-col gap-2">
              {displayedAssociations.map((association, displayIdx) => {
                // Calculate the original index in the full associations array
                const originalIdx = showAllAssociationItems
                  ? displayIdx
                  : displayIdx;
                const isExpanded = expandedAssociations[originalIdx];
                return (
                  <div
                    key={originalIdx}
                    className="group flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-purple-600/5 via-purple-500/5 to-pink-500/5 dark:from-cyan-900/10 dark:via-cyan-800/10 dark:to-emerald-800/10 border border-purple-200/30 dark:border-cyan-800/30 hover:border-purple-300 dark:hover:border-emerald-700 hover:shadow-sm transition-all duration-200"
                  >
                    {/* User Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 dark:from-cyan-500 dark:to-emerald-500 flex items-center justify-center">
                        <CircleUser className="w-5 h-5 text-white" />
                      </div>
                    </div>

                    {/* Association Text */}
                    <div className="flex-1 min-w-0">
                      <div
                        ref={(el) => {
                          associationRefs.current[originalIdx] = el;
                        }}
                        className={`text-sm text-gray-800 dark:text-gray-200 leading-6 transition-all duration-200 ${
                          !isExpanded ? "line-clamp-3" : ""
                        }`}
                        style={{
                          display: !isExpanded ? "-webkit-box" : "block",
                          WebkitLineClamp: !isExpanded ? MAX_LINES : undefined,
                          WebkitBoxOrient: !isExpanded ? "vertical" : undefined,
                          overflow: !isExpanded ? "hidden" : "visible",
                        }}
                      >
                        {association}
                      </div>
                      {needsShowMoreAssociations[originalIdx] && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAssociation(originalIdx);
                          }}
                          className="mt-2 text-sm font-medium text-purple-600 dark:text-cyan-400 hover:text-purple-700 dark:hover:text-emerald-300 transition-colors"
                        >
                          {isExpanded ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {totalAssociations > INITIAL_ASSOCIATIONS_COUNT && (
                <button
                  onClick={() => setShowAllAssociationItems((v) => !v)}
                  className="mt-1 self-center inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-cyan-400 hover:text-purple-700 dark:hover:text-emerald-300"
                >
                  {showAllAssociationItems ? (
                    <>
                      Show less <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Show more (
                      {totalAssociations - INITIAL_ASSOCIATIONS_COUNT})
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
